import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Bot, Check, Loader2, Send, Sparkles, TrendingUp, X } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { aiAgentApi, type AiAgentChart, type AiAgentResponse, type AiAgentStructuredBlocks } from '../../../lib/api';
import { kiAuswertungenApi, type KiAuswertung } from '../../../lib/api/ai';
import { saveBlob } from '../../../lib/api/billing';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

type Message = {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  markdown?: string;
  sources?: string[];
  chart?: AiAgentChart | null;
  structuredBlocks?: AiAgentStructuredBlocks;
  chainMeta?: { iterations: number; executed: number; stoppedBy: string | null };
};

type PendingAction = {
  sessionId: string;
  token: string;
  label: string;
};

const randomId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const numberFormatter = new Intl.NumberFormat('de-DE');

function formatChartNumber(value: number | string): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return numberFormatter.format(n);
}

function formatDateToken(token: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(token);
  if (!m) return token;
  const yyyy = Number(m[1]);
  const mm = Number(m[2]) - 1;
  const dd = Number(m[3]);
  const dt = new Date(Date.UTC(yyyy, mm, dd));
  if (Number.isNaN(dt.getTime())) return token;
  return dt.toLocaleDateString('de-DE');
}

function formatChartLabel(raw: string): string {
  const normalized = String(raw || '').trim();
  const sep = ' - ';
  if (normalized.includes(sep)) {
    const [from, to] = normalized.split(sep);
    return `${formatDateToken(from)} - ${formatDateToken(to)}`;
  }
  return formatDateToken(normalized);
}

function extractActionLabel(response: AiAgentResponse): string {
  const proposed = response.proposedAction;
  if (!proposed) return 'Aktion bestätigen';
  return proposed.label || `${proposed.name} ausführen`;
}

/** CSV aus der Bestätigung (T-0198) im Browser speichern. */
function saveDownload(download: NonNullable<AiAgentResponse['download']>): void {
  const bytes = Uint8Array.from(atob(download.base64), (c) => c.charCodeAt(0));
  saveBlob(new Blob([bytes], { type: download.mimeType }), download.fileName);
}

function StructuredBlocksView({
  blocks,
  onFollowup,
}: {
  readonly blocks: AiAgentStructuredBlocks;
  readonly onFollowup: (question: string) => void;
}) {
  return (
    <div className="mt-3 space-y-2">
      {blocks.kpis.length > 0 ? (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {blocks.kpis.slice(0, 6).map((kpi, idx) => (
            <div key={`kpi-${idx}`} className="rounded-md border bg-background/60 p-2">
              <div className="text-[11px] text-muted-foreground">{kpi.label}</div>
              <div className="text-base font-semibold">{formatChartNumber(kpi.value)}</div>
            </div>
          ))}
        </div>
      ) : null}
      {blocks.insights.length > 0 ? (
        <div className="rounded-md border bg-background/60 p-2">
          <div className="mb-1 text-xs font-semibold text-muted-foreground">Insights</div>
          <ul className="list-disc space-y-1 pl-4 text-xs">
            {blocks.insights.slice(0, 4).map((insight, idx) => (
              <li key={`insight-${idx}`}>{insight}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {blocks.warnings.length > 0 ? (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs">
          <div className="mb-1 font-semibold">Hinweise</div>
          <ul className="list-disc space-y-1 pl-4">
            {blocks.warnings.slice(0, 4).map((warning, idx) => (
              <li key={`warning-${idx}`}>{warning}</li>
            ))}
          </ul>
        </div>
      ) : null}
      <div className="flex flex-wrap gap-2">
        {blocks.actions.slice(0, 4).map((action, idx) => (
          <Badge key={`action-${idx}`} variant="secondary" className="text-[11px]">
            {action}
          </Badge>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        {blocks.followups.slice(0, 3).map((followup, idx) => (
          <Button
            key={`followup-${idx}`}
            type="button"
            size="sm"
            variant="outline"
            className="h-7 text-[11px]"
            onClick={() => onFollowup(followup)}
          >
            {followup}
          </Button>
        ))}
      </div>
      <div className="text-[11px] text-muted-foreground">
        Confidence: {Math.round((blocks.confidence || 0) * 100)}%
      </div>
      {blocks.blocks.map((block, idx) => {
        if (block?.type === 'heatmap') {
          const matrix = Array.isArray(block.matrix) ? block.matrix : [];
          return (
            <div key={`block-${idx}`} className="rounded-md border bg-background/60 p-2">
              <div className="mb-2 text-xs font-semibold text-muted-foreground">{String(block.title || 'Heatmap')}</div>
              <div className="grid grid-cols-1 gap-1 text-[11px]">
                {matrix.slice(0, 18).map((cell: any, cellIdx: number) => (
                  <div key={`cell-${cellIdx}`} className="flex items-center justify-between rounded border px-2 py-1">
                    <span>{String(cell?.x || '-')} x {String(cell?.y || '-')}</span>
                    <span className="font-semibold">{formatChartNumber(Number(cell?.value || 0))}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        }
        if (block?.type === 'table') {
          const items = Array.isArray(block.items) ? block.items : [];
          return (
            <div key={`block-${idx}`} className="rounded-md border bg-background/60 p-2">
              <div className="mb-2 text-xs font-semibold text-muted-foreground">{String(block.title || 'Details')}</div>
              <div className="space-y-1 text-[11px]">
                {items.slice(0, 8).map((row: any, rowIdx: number) => (
                  <div key={`row-${rowIdx}`} className="rounded border px-2 py-1">
                    {Object.entries(row || {})
                      .slice(0, 3)
                      .map(([k, v]) => `${k}: ${String(v)}`)
                      .join(' · ')}
                  </div>
                ))}
              </div>
            </div>
          );
        }
        if (block?.type === 'comparison') {
          return (
            <div key={`block-${idx}`} className="rounded-md border bg-background/60 p-2">
              <div className="mb-2 text-xs font-semibold text-muted-foreground">{String(block.title || 'Vergleich')}</div>
              <div className="text-[11px] whitespace-pre-wrap text-muted-foreground">{String(block.summary || '')}</div>
            </div>
          );
        }
        return null;
      })}
    </div>
  );
}

function InlineChart({
  chart,
  onDrilldown,
}: {
  readonly chart: AiAgentChart;
  readonly onDrilldown?: (ctx: { label: string; series: string; value: number }) => void;
}) {
  const labels = chart.labels || [];
  const series = chart.series || [];
  const data = labels.map((label, index) => {
    const row: Record<string, string | number> = { label };
    series.forEach((s, seriesIndex) => {
      row[`series_${seriesIndex}`] = Number(s.values?.[index] ?? 0);
    });
    return row;
  });
  const colors = ['#5aac86', '#4f46e5', '#06b6d4', '#f59e0b', '#ef4444'];
  const primarySeries = series[0];
  const primaryValues = (primarySeries?.values || []).map((v) => Number(v || 0));
  const first = primaryValues.length > 0 ? primaryValues[0] : 0;
  const last = primaryValues.at(-1) ?? 0;
  const delta = last - first;
  const peak = primaryValues.length > 0 ? Math.max(...primaryValues) : 0;
  let trendLabel = 'Seitwaertstrend';
  if (delta > 0) {
    trendLabel = 'Aufwaertstrend';
  } else if (delta < 0) {
    trendLabel = 'Abwaertstrend';
  }

  const handleDrilldown = (payload: any) => {
    if (!onDrilldown) return;
    const index = Number(payload?.activeTooltipIndex ?? payload?.activeIndex ?? -1);
    if (!Number.isFinite(index) || index < 0 || index >= labels.length) return;
    const label = String(labels[index] ?? '');
    const seriesName = String(series[0]?.name ?? 'Wert');
    const value = Number(series[0]?.values?.[index] ?? 0);
    onDrilldown({ label, series: seriesName, value });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10, scale: 0.985 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.45, ease: [0.2, 0.8, 0.2, 1] }}
      className="relative mt-3 overflow-hidden rounded-xl border border-primary/30 bg-gradient-to-br from-primary/10 via-background/70 to-background/80 p-3 shadow-[0_0_30px_-18px_rgba(90,172,134,0.9)] transition-all duration-300 hover:shadow-[0_0_42px_-16px_rgba(90,172,134,0.95)]"
    >
      <motion.div
        aria-hidden
        initial={{ opacity: 0 }}
        animate={{ opacity: [0.05, 0.18, 0.05] }}
        transition={{ duration: 1.4, ease: 'easeInOut' }}
        className="pointer-events-none absolute inset-0 rounded-xl bg-[radial-gradient(circle_at_20%_20%,rgba(90,172,134,0.35),transparent_45%)]"
      />
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-2 py-1 text-[11px] font-semibold text-primary">
          <Sparkles className="h-3 w-3" />
          Insight
        </div>
        <div className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-1 text-[11px] text-emerald-600">
          <TrendingUp className="h-3 w-3" />
          {trendLabel}
        </div>
        <div className="rounded-full border bg-background/70 px-2 py-1 text-[11px] text-muted-foreground">
          Peak: {formatChartNumber(peak)}
        </div>
      </div>
      <div className="mb-2 text-xs font-semibold tracking-wide text-muted-foreground">{chart.title}</div>
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          {chart.type === 'bar' ? (
            <BarChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }} onClick={handleDrilldown}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} tickFormatter={(value) => formatChartLabel(String(value))} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(value) => formatChartNumber(Number(value))} />
              <Tooltip
                labelFormatter={(label) => formatChartLabel(String(label))}
                formatter={(value: number | string, name: string) => [formatChartNumber(value), name]}
                contentStyle={{
                  borderRadius: 12,
                  border: '1px solid rgba(90,172,134,0.35)',
                  background: 'rgba(15,23,42,0.9)',
                  backdropFilter: 'blur(6px)',
                }}
                labelStyle={{ color: '#cbd5e1', fontWeight: 600 }}
                itemStyle={{ color: '#f8fafc' }}
              />
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
              {series.map((s, idx) => (
                <Bar
                  key={`bar-${s.name}-${idx}`}
                  dataKey={`series_${idx}`}
                  name={s.name}
                  fill={colors[idx % colors.length]}
                  radius={[4, 4, 0, 0]}
                  isAnimationActive
                  animationDuration={850}
                  animationEasing="ease-out"
                />
              ))}
            </BarChart>
          ) : (
            <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }} onClick={handleDrilldown}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} tickFormatter={(value) => formatChartLabel(String(value))} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(value) => formatChartNumber(Number(value))} />
              <Tooltip
                labelFormatter={(label) => formatChartLabel(String(label))}
                formatter={(value: number | string, name: string) => [formatChartNumber(value), name]}
                contentStyle={{
                  borderRadius: 12,
                  border: '1px solid rgba(90,172,134,0.35)',
                  background: 'rgba(15,23,42,0.9)',
                  backdropFilter: 'blur(6px)',
                }}
                labelStyle={{ color: '#cbd5e1', fontWeight: 600 }}
                itemStyle={{ color: '#f8fafc' }}
              />
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
              {series.map((s, idx) => (
                <Line
                  key={`line-${s.name}-${idx}`}
                  type="monotone"
                  dataKey={`series_${idx}`}
                  name={s.name}
                  stroke={colors[idx % colors.length]}
                  strokeWidth={3}
                  dot={{ r: 1.5, strokeWidth: 0, fill: colors[idx % colors.length] }}
                  activeDot={{ r: 5, stroke: colors[idx % colors.length], strokeWidth: 2, fill: '#fff' }}
                  isAnimationActive
                  animationDuration={900}
                  animationEasing="ease-out"
                  style={{ filter: 'drop-shadow(0 0 6px rgba(90,172,134,0.45))' }}
                />
              ))}
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>
    </motion.div>
  );
}

export default function AiAssistantChat() {
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState<string>('');
  const [mode, setMode] = useState<'chat' | 'data'>('data');
  const [comparisonMode, setComparisonMode] = useState(false);
  const [comparisonSessionId, setComparisonSessionId] = useState<string>('');
  const [streamingMode, setStreamingMode] = useState(false);
  const [streamStatus, setStreamStatus] = useState<string>('');
  const [messages, setMessages] = useState<Message[]>([
    {
      id: randomId(),
      role: 'assistant',
      text: 'Hallo! Im Datenmodus nutze ich Tools fuer Live-Daten. Im Chatmodus antworte ich ohne Tool-Calls. Schreibende Aktionen fuehre ich erst nach deiner Bestaetigung aus.',
    },
  ]);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [gespeichert, setGespeichert] = useState<KiAuswertung[] | null>(null);

  const gespeicherteLaden = async () => {
    try {
      setGespeichert(await kiAuswertungenApi.list());
    } catch {
      setGespeichert(null);
    }
  };

  useEffect(() => {
    void gespeicherteLaden();
  }, []);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const canSend = useMemo(() => !loading && !confirming && input.trim() !== '', [confirming, input, loading]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
  }, [messages, loading, confirming, pendingAction]);

  const appendMessage = (message: Message) => {
    setMessages((prev) => [...prev, message]);
  };

  const submitFollowupQuestion = (question: string) => {
    setInput(question);
  };

  const buildDrilldownPrompt = (ctx: { label: string; series: string; value: number }) =>
    `Drilldown fuer ${ctx.series} bei ${ctx.label} (Wert ${ctx.value})`;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || loading || confirming) return;

    setInput('');
    appendMessage({ id: randomId(), role: 'user', text });
    setLoading(true);
    try {
      const payload = {
        message: text,
        sessionId: sessionId || undefined,
        mode,
        historyLimit: 16,
        comparisonSessionId: comparisonMode ? (comparisonSessionId || sessionId || undefined) : undefined,
      } as const;

      if (streamingMode) {
        setStreamStatus('Starte Streaming...');
        let streamedResponse: AiAgentResponse | null = null;
        await aiAgentApi.queryStream(payload, (chunk) => {
          if (chunk.type === 'progress') {
            setStreamStatus(chunk.message || chunk.stage || 'Verarbeite...');
            return;
          }
          if (chunk.type === 'result' && chunk.data) {
            streamedResponse = chunk.data;
          }
          if (chunk.type === 'done') {
            setStreamStatus('');
          }
        });
        const response = streamedResponse;
        if (!response) {
          throw new Error('Keine Streaming-Antwort erhalten');
        }
        if (!sessionId && response.sessionId) {
          setSessionId(response.sessionId);
        }
        appendMessage({
          id: randomId(),
          role: 'assistant',
          text: response.answer,
          markdown: response.answerMarkdown || response.answer,
          sources: response.sources,
          chart: response.chart,
          structuredBlocks: response.structuredBlocks,
          chainMeta: response.chainMeta,
        });
        if (response.requiresConfirmation && response.pendingActionToken) {
          setPendingAction({
            sessionId: response.sessionId,
            token: response.pendingActionToken,
            label: extractActionLabel(response),
          });
        } else {
          setPendingAction(null);
        }
      } else {
        const response = await aiAgentApi.query(payload);

        if (!sessionId && response.sessionId) {
          setSessionId(response.sessionId);
        }
        appendMessage({
          id: randomId(),
          role: 'assistant',
          text: response.answer,
          markdown: response.answerMarkdown || response.answer,
          sources: response.sources,
          chart: response.chart,
          structuredBlocks: response.structuredBlocks,
          chainMeta: response.chainMeta,
        });

        if (response.requiresConfirmation && response.pendingActionToken) {
          setPendingAction({
            sessionId: response.sessionId,
            token: response.pendingActionToken,
            label: extractActionLabel(response),
          });
        } else {
          setPendingAction(null);
        }
      }
    } catch (error: any) {
      toast.error(error?.message || 'KI-Anfrage fehlgeschlagen');
      appendMessage({
        id: randomId(),
        role: 'assistant',
        text: 'Es ist ein Fehler aufgetreten. Bitte versuche es erneut.',
      });
    } finally {
      setLoading(false);
      setStreamStatus('');
    }
  };

  const runConfirmation = async (confirm: boolean) => {
    if (!pendingAction || confirming) return;
    setConfirming(true);
    try {
      const response = await aiAgentApi.confirm({
        sessionId: pendingAction.sessionId,
        pendingActionToken: pendingAction.token,
        confirm,
      });
      appendMessage({
        id: randomId(),
        role: 'assistant',
        text: response.answer,
        markdown: response.answerMarkdown || response.answer,
        sources: response.sources,
        chart: response.chart,
        structuredBlocks: response.structuredBlocks,
        chainMeta: response.chainMeta,
      });
      if (response.download) saveDownload(response.download);
      if (confirm) void gespeicherteLaden();
      setPendingAction(null);
    } catch (error: any) {
      toast.error(error?.message || 'Bestätigung fehlgeschlagen');
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5" />
            KI-Chat (Office)
          </CardTitle>
          <CardDescription>
            Dual-Mode: Datenmodus (Tools/DB) und Chatmodus (ohne Tools). Schreibende Aktionen laufen nur mit Confirm-Step.
          </CardDescription>
        </CardHeader>
      </Card>

      <Card>
        <CardContent className="space-y-4 p-4">
          <div ref={scrollContainerRef} className="max-h-[60vh] space-y-3 overflow-y-auto pr-1">
            <div className="flex gap-2">
              <Button
                type="button"
                variant={mode === 'data' ? 'default' : 'outline'}
                size="sm"
                disabled={loading || confirming}
                onClick={() => setMode('data')}
              >
                Datenmodus
              </Button>
              <Button
                type="button"
                variant={mode === 'chat' ? 'default' : 'outline'}
                size="sm"
                disabled={loading || confirming}
                onClick={() => setMode('chat')}
              >
                Chatmodus
              </Button>
              <Button
                type="button"
                variant={comparisonMode ? 'default' : 'outline'}
                size="sm"
                disabled={loading || confirming}
                onClick={() => {
                  const next = !comparisonMode;
                  setComparisonMode(next);
                  if (next) {
                    setComparisonSessionId(sessionId);
                  }
                }}
              >
                Vergleich
              </Button>
              <Button
                type="button"
                variant={streamingMode ? 'default' : 'outline'}
                size="sm"
                disabled={loading || confirming}
                onClick={() => setStreamingMode((prev) => !prev)}
              >
                Streaming
              </Button>
            </div>

            {messages.map((message) => {
              const rowClassName = message.role === 'user' ? 'flex justify-end' : 'flex justify-start';
              const isWideAssistantChart = message.role === 'assistant' && !!message.chart;
              let bubbleClassName = 'max-w-[85%] rounded-lg border bg-card px-3 py-2';
              if (message.role === 'user') {
                bubbleClassName = 'max-w-[85%] rounded-lg bg-primary px-3 py-2 text-primary-foreground';
              } else if (isWideAssistantChart) {
                bubbleClassName = 'w-full rounded-lg border bg-card px-3 py-2';
              }

              return (
                <div key={message.id} className={rowClassName}>
                  <div className={bubbleClassName}>
                    {message.role === 'assistant' ? (
                      <div className="text-sm leading-relaxed">
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                            ul: ({ children }) => <ul className="mb-2 list-disc space-y-1 pl-5 last:mb-0">{children}</ul>,
                            ol: ({ children }) => <ol className="mb-2 list-decimal space-y-1 pl-5 last:mb-0">{children}</ol>,
                            li: ({ children }) => <li>{children}</li>,
                            strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                            code: ({ children }) => <code className="rounded bg-muted px-1 py-0.5 text-xs">{children}</code>,
                          }}
                        >
                          {message.markdown || message.text}
                        </ReactMarkdown>
                      </div>
                    ) : (
                      <div className="whitespace-pre-wrap text-sm">{message.text}</div>
                    )}
                    {message.chart ? (
                      <InlineChart
                        chart={message.chart}
                        onDrilldown={(ctx) => {
                          setInput(buildDrilldownPrompt(ctx));
                        }}
                      />
                    ) : null}
                    {message.structuredBlocks ? (
                      <StructuredBlocksView
                        blocks={message.structuredBlocks}
                        onFollowup={(question) => submitFollowupQuestion(question)}
                      />
                    ) : null}
                    {message.chainMeta ? (
                      <div className="mt-2 text-[11px] text-muted-foreground">
                        Chain: {message.chainMeta.executed} Tools in {message.chainMeta.iterations} Iterationen
                        {message.chainMeta.stoppedBy ? ` (${message.chainMeta.stoppedBy})` : ''}
                      </div>
                    ) : null}
                    {message.sources && message.sources.length > 0 ? (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {message.sources.map((source) => (
                          <Badge variant="outline" key={`${message.id}-${source}`} className="text-xs">
                            {source}
                          </Badge>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })}
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                {streamStatus || 'KI verarbeitet die Anfrage...'}
              </div>
            ) : null}
          </div>

          {pendingAction ? (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3">
              <div className="text-sm font-medium">{pendingAction.label}</div>
              <div className="mt-2 flex flex-wrap gap-2">
                <Button size="sm" onClick={() => runConfirmation(true)} disabled={confirming}>
                  {confirming ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
                  Bestätigen
                </Button>
                <Button size="sm" variant="outline" onClick={() => runConfirmation(false)} disabled={confirming}>
                  <X className="mr-2 h-4 w-4" />
                  Verwerfen
                </Button>
              </div>
            </div>
          ) : null}

          <form onSubmit={handleSubmit} className="flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={mode === 'data'
                ? 'Beispiel: "Welche Verträge hat BS-12345?" oder "Trend der Probestunden der letzten 8 Wochen"'
                : 'Beispiel: "Wie kann ich Kündigungen besser kommunizieren?"'}
              disabled={loading || confirming}
            />
            <Button type="submit" disabled={!canSend}>
              <Send className="mr-2 h-4 w-4" />
              Senden
            </Button>
          </form>
        </CardContent>
      </Card>

      {gespeichert && gespeichert.length > 0 ? (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Gespeicherte Auswertungen</CardTitle>
            <CardDescription>Frage und Antwort, so wie sie beim Speichern waren.</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="divide-y rounded-md border">
              {gespeichert.map((eintrag) => (
                <li key={eintrag.id} className="flex flex-wrap items-center justify-between gap-2 p-2 text-sm">
                  <button
                    type="button"
                    className="text-left hover:underline"
                    onClick={() =>
                      appendMessage({
                        id: randomId(),
                        role: 'assistant',
                        text: eintrag.antwort,
                        markdown: `**${eintrag.name}** – ${eintrag.frage}\n\n${eintrag.antwort}`,
                      })
                    }
                  >
                    <span className="font-medium">{eintrag.name}</span>{' '}
                    <span className="text-muted-foreground">
                      · {new Date(eintrag.createdAt).toLocaleDateString('de-DE')}
                    </span>
                  </button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={async () => {
                      try {
                        await kiAuswertungenApi.remove(eintrag.id);
                        await gespeicherteLaden();
                      } catch (error) {
                        toast.error(error instanceof Error ? error.message : 'Löschen fehlgeschlagen');
                      }
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

